// The API error body's `code` mapped to a message (P1-E05): `i18n_key` is `errors.<code>` in the common
// namespace; unknown codes fall back by family (…_not_found, …_unavailable) and then to a generic line.
import type { TFunction } from 'i18next';
import { ApiError } from '@/api/client';
import { formatNumber } from '@/lib/format';

type T = TFunction<'common'>;

export function errorText(err: unknown, t: T): string {
  const tt = t as unknown as (k: string, o?: Record<string, unknown>) => string;
  if (!(err instanceof ApiError)) return t('errors.network_error');
  const { code, i18n_key, params } = err.body;
  const fallback = code.endsWith('_not_found')
    ? t('errors.not_found')
    : code.endsWith('_unavailable')
      ? t('errors.service_unavailable')
      : err.status >= 500
        ? t('errors.unexpected_response')
        : t('errors.request_failed');
  const general = tt(i18n_key || `errors.${code}`, { ...params, defaultValue: fallback });
  // A 422 on a field says which field and why («Промпт: не больше 200 000 символов»), not only «check the fields».
  if (code === 'validation_error') {
    const field = errorField(err);
    const first = (params.errors as { type?: unknown; msg?: unknown }[] | undefined)?.[0];
    if (field) {
      const max = typeof first?.msg === 'string' ? first.msg.match(/at most ([0-9]+)/)?.[1] : undefined;
      const reason = typeof first?.type === 'string' ? tt(`validation.${first.type}`, { max: max ? formatNumber(Number(max)) : '', defaultValue: '' }) : '';
      return tt('errors.validation_field', { field: tt(`fields.${field}`, { defaultValue: field }), reason: reason || general, defaultValue: general });
    }
  }
  // A limit names its kind (P4-B09 `plan_limit_reached` with `params.kind`): a message of its own when there is one.
  const kind = typeof params.kind === 'string' ? params.kind : null;
  return kind ? tt(`errors.${code}_${kind}`, { ...params, defaultValue: general }) : general;
}

/** The field a 422 points at (`params.field`, or the first `params.errors[].loc` after "body"). */
export function errorField(err: unknown): string | null {
  if (!(err instanceof ApiError) || err.status !== 422) return null;
  const p = err.body.params as { field?: unknown; errors?: { loc?: unknown[] }[] };
  if (typeof p.field === 'string') return p.field;
  const loc = p.errors?.[0]?.loc;
  const last = Array.isArray(loc) ? loc.filter((x) => x !== 'body').at(-1) : undefined;
  return typeof last === 'string' ? last : null;
}
